<script setup lang="ts">
import { computed, ref, } from 'vue'
import { storeToRefs } from 'pinia'

import ChipsCount from '../shared/ChipsCount.vue'
import RaiseSlider from './raiseSlider/RaiseSlider.vue'

import GameButton from '@/components/shared/GameButton.vue'

import { useGameState } from '@/stores/gameState'
import { useUiState } from '@/stores/uiState'

import { useInHandActions } from '@/composables/useInHandActions'

interface Props {
  canAct?: boolean
  isShowdown?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  canAct: true,
  isShowdown: false
})

const {
  isMyTurn,
  autoAction,
  callAmount,

  legalRaiseRange,
  legalActions,
  raiseLevels,
  winners,
  player
} = storeToRefs(useGameState())
const { requestAutoAction, sitIn } = useGameState()
const uiState = useUiState()

const { isPending, bet, raise, fold, call, check } = useInHandActions()

const isPlayerSittingOut = computed(() => player.value?.states?.includes('sit_out'))

// actions
const isCheck = computed(() => legalActions.value.includes('check'))
const isCall = computed(() => legalActions.value.includes('call'))
const isBet = computed(() => legalActions.value.includes('bet'))
const betRaiseActionList = computed(() =>
  raiseLevels.value
    .filter((el) => el.amount > legalRaiseRange.value.min)
    .filter((el) => el.amount < legalRaiseRange.value.max)
    .sort((a, b) => b.amount - a.amount)
)
const isBetOrRaiseAllowed = computed(
  () => legalActions.value.includes('bet') || legalActions.value.includes('raise')
)

function betOrRaise(amount: number): void {
  isBet.value ? bet(amount) : raise(amount)
}

const sliderValue = ref([legalRaiseRange.value.min])
const makeBetFromSlider = () => {
  document.activeElement?.blur()
  document.addEventListener(
    'pointerup',
    (e) => {
      const THRESHOLD = 75
      const delta = document.body.clientHeight - e.clientY
      if (delta > THRESHOLD) {
        const amount = sliderValue.value[0]
        if (amount >= legalRaiseRange.value.min) {
          betOrRaise(amount)
        }
      }
    },
    { once: true }
  )
}

const handleSitIn = () => {
  sitIn();
  if (props.isShowdown) return;
  uiState.modals.attention.open({
    titleKey: 'TableSitInAttentionModal.label.Title',
    descriptionKey: 'TableSitInAttentionModal.label.Description',
    buttonKey: 'TableSitInAttentionModal.button.Okay'
  })
}
</script>

<template>
  <div class="hand-actions-mobile">
    <!-- Show all controls only if user can act -->
    <template v-if="props.canAct && (!isMyTurn || (isMyTurn && !autoAction))">
      <game-button v-if="!isMyTurn" :disabled="isPending"
        class="hand-actions-mobile__actions-button hand-actions-mobile__actions-button--inner" variant="secondary-auto"
        :auto-action-active="autoAction === 'fold'" @click="requestAutoAction('fold')">
        {{
          callAmount === 0
            ? `${$t('PokerTable.button.Check')} / ${$t('PokerTable.button.Fold')}`
            : $t('PokerTable.button.Fold')
        }}
      </game-button>
      <game-button v-if="isMyTurn" class="hand-actions-mobile__actions-button" :disabled="isPending" variant="primary"
        @click="fold">
        {{ $t('PokerTable.button.Fold') }}
      </game-button>

      <game-button v-if="!isMyTurn && !callAmount" :disabled="isPending"
        class="hand-actions-mobile__actions-button hand-actions-mobile__actions-button--inner" variant="secondary-auto"
        :auto-action-active="autoAction === 'check'" @click="requestAutoAction('check')">
        {{ $t('PokerTable.button.Check') }}
      </game-button>

      <template v-if="isMyTurn">
        <game-button v-if="isCheck" class="hand-actions-mobile__actions-button" :disabled="isPending" variant="primary"
          @click="check">
          <template #default> {{ $t('PokerTable.button.Check') }} </template>
        </game-button>

        <game-button v-if="isCall" :disabled="isPending" variant="primary"
          class="hand-actions-mobile__actions-button hand-actions-mobile__actions-button--inner" @click="call">
          <template v-if="callAmount < legalRaiseRange.max" #left>
            {{ $t('PokerTable.button.Call') }}
          </template>
          <template v-if="callAmount >= legalRaiseRange.max" #default>
            {{ $t('PokerTable.button.AllIn') }}
          </template>
          <template v-else #default>
            <chips-count :value="callAmount" class="hand-actions-mobile__count" />
          </template>
        </game-button>

        <raise-slider v-if="isMyTurn && isBetOrRaiseAllowed" class="hand-actions-mobile__raise-slider"
          v-model="sliderValue" :min="legalRaiseRange.min" :max="legalRaiseRange.max"
          @vue:before-mount="sliderValue = [legalRaiseRange.min]" @done="makeBetFromSlider" />
      </template>
      <div class="hand-actions-mobile__raise-buttons">
        <template v-if="isMyTurn && isBetOrRaiseAllowed && !isPending">
          <game-button v-if="isBetOrRaiseAllowed && betRaiseActionList.length < 4" variant="mobile-action"
            class="hand-actions-mobile__actions-button" @click="betOrRaise(legalRaiseRange.max)">
            {{ $t('PokerTable.button.AllIn') }}
          </game-button>

          <template v-for="brAction in betRaiseActionList" :key="brAction.amount">
            <game-button variant="mobile-action" class="hand-actions-mobile__actions-button"
              @click="betOrRaise(brAction.amount)">
              <template #default>
                <chips-count :value="brAction.amount" class="hand-actions-mobile__count" />
              </template>
              <template #left>
                {{ isBet ? $t('PokerTable.button.Bet') : $t('PokerTable.button.Raise') }}
              </template>
              <template #right> {{ brAction.factor * 100 }}% </template>
            </game-button>
          </template>
        </template>
      </div>
    </template>

    <game-button v-if="isPlayerSittingOut" :disabled="isPending" variant="primary" @click="handleSitIn">
      {{ $t('PokerTable.button.SitIn') }}
    </game-button>
  </div>
</template>

<style lang="scss">
.hand-actions-mobile {
  position: relative;
  display: flex;
  gap: 10px;
  left: 20px;
  align-items: flex-end;

  &__raise-buttons {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    z-index: 2;

    button {
      width: 100%;
      flex-basis: auto;
    }
  }

  &__actions-button {
    font-size: 11px;
    color: #d6ddda;
    opacity: 0.8;
    border-radius: 6px;
    flex: 1;
    padding: 0;

    &--inner {
      max-width: 33%;
    }
  }

  &__raise-slider-button:disabled {
    color: gray;
    cursor: not-allowed;
  }

  &__count {
    font-weight: 600;
  }

  &__raise-slider {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: 50px;
    z-index: 1;
  }
}
</style>
